/**
 * Fractional indexing for z-order.
 *
 * Two users reordering different objects at the same time must not fight over
 * an integer "layer" column, so paint order is a *string* key and sorting is
 * lexicographic. Inserting between two neighbours only ever writes one node.
 *
 * Implementation follows the well-known base-62 scheme: a leading magnitude
 * byte encodes the integer part length, the remainder is the fraction.
 */

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length; // 62
const SMALLEST = DIGITS[0]!; // '0'
const LARGEST = DIGITS[BASE - 1]!; // 'z'
const ZERO = 'a0';

function midpoint(lower: string, upper: string | null): string {
  if (upper !== null && lower >= upper) {
    throw new Error(`fractional index out of order: ${lower} >= ${upper}`);
  }
  if (lower.endsWith(SMALLEST) || (upper !== null && upper.endsWith(SMALLEST))) {
    throw new Error(`fractional index must not end in ${SMALLEST}`);
  }

  if (upper !== null) {
    // Copy the shared prefix, then recurse on the remainder.
    let i = 0;
    while ((lower[i] ?? SMALLEST) === upper[i]) i++;
    if (i > 0) {
      return upper.slice(0, i) + midpoint(lower.slice(i), upper.slice(i));
    }
  }

  const lowDigit = lower === '' ? 0 : DIGITS.indexOf(lower[0]!);
  const highDigit = upper === null ? BASE : DIGITS.indexOf(upper[0]!);

  if (highDigit - lowDigit > 1) {
    const mid = Math.round(0.5 * (lowDigit + highDigit));
    return DIGITS[mid]!;
  }

  if (upper !== null && upper.length > 1) {
    return upper.slice(0, 1);
  }

  // Nothing free at this digit: descend one level.
  return DIGITS[lowDigit]! + midpoint(lower.slice(1), null);
}

function validateInteger(int: string): void {
  if (int.length !== integerLength(int[0]!)) {
    throw new Error(`invalid fractional index integer part: ${int}`);
  }
}

function integerLength(head: string): number {
  if (head >= 'a' && head <= 'z') return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2;
  if (head >= 'A' && head <= 'Z') return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2;
  throw new Error(`invalid fractional index head: ${head}`);
}

function getIntegerPart(key: string): string {
  const len = integerLength(key[0]!);
  if (len > key.length) throw new Error(`invalid fractional index: ${key}`);
  return key.slice(0, len);
}

function incrementInteger(int: string): string | null {
  validateInteger(int);
  const head = int[0]!;
  const digits = int.slice(1).split('');
  let carry = true;
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    const d = DIGITS.indexOf(digits[i]!) + 1;
    if (d === BASE) {
      digits[i] = SMALLEST;
    } else {
      digits[i] = DIGITS[d]!;
      carry = false;
    }
  }
  if (carry) {
    // Overflowed: widen the magnitude byte.
    if (head === 'Z') return `a${SMALLEST}`;
    if (head === 'z') return null;
    const nextHead = String.fromCharCode(head.charCodeAt(0) + 1);
    const nextLen = integerLength(nextHead);
    return nextHead + SMALLEST.repeat(nextLen - 1);
  }
  return head + digits.join('');
}

function decrementInteger(int: string): string | null {
  validateInteger(int);
  const head = int[0]!;
  const digits = int.slice(1).split('');
  let borrow = true;
  for (let i = digits.length - 1; borrow && i >= 0; i--) {
    const d = DIGITS.indexOf(digits[i]!) - 1;
    if (d === -1) {
      digits[i] = LARGEST;
    } else {
      digits[i] = DIGITS[d]!;
      borrow = false;
    }
  }
  if (borrow) {
    if (head === 'a') return `Z${LARGEST}`;
    if (head === 'A') return null;
    const prevHead = String.fromCharCode(head.charCodeAt(0) - 1);
    const prevLen = integerLength(prevHead);
    return prevHead + LARGEST.repeat(prevLen - 1);
  }
  return head + digits.join('');
}

/**
 * Returns a key that sorts strictly between `a` and `b`.
 * Pass `null` for an open end: `keyBetween(null, first)` prepends,
 * `keyBetween(last, null)` appends.
 */
export function keyBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) {
    throw new Error(`fractional index out of order: ${a} >= ${b}`);
  }

  if (a === null) {
    if (b === null) return ZERO;
    const ib = getIntegerPart(b);
    const fb = b.slice(ib.length);
    if (ib === `A${SMALLEST.repeat(integerLength('A') - 1)}`) {
      return ib + midpoint('', fb);
    }
    if (ib < b) return ib;
    const dec = decrementInteger(ib);
    if (dec === null) throw new Error('fractional index underflow');
    return dec;
  }

  if (b === null) {
    const ia = getIntegerPart(a);
    const fa = a.slice(ia.length);
    const inc = incrementInteger(ia);
    return inc === null ? ia + midpoint(fa, null) : inc;
  }

  const ia = getIntegerPart(a);
  const fa = a.slice(ia.length);
  const ib = getIntegerPart(b);
  const fb = b.slice(ib.length);

  if (ia === ib) return ia + midpoint(fa, fb);

  const inc = incrementInteger(ia);
  if (inc === null) throw new Error('fractional index overflow');
  if (inc < b) return inc;
  return ia + midpoint(fa, null);
}

/** n evenly spread keys between a and b, for pasting or importing in bulk. */
export function keysBetween(a: string | null, b: string | null, n: number): string[] {
  if (n <= 0) return [];
  if (n === 1) return [keyBetween(a, b)];
  if (b === null) {
    let c = keyBetween(a, b);
    const out = [c];
    for (let i = 1; i < n; i++) {
      c = keyBetween(c, b);
      out.push(c);
    }
    return out;
  }
  if (a === null) {
    let c = keyBetween(a, b);
    const out = [c];
    for (let i = 1; i < n; i++) {
      c = keyBetween(a, c);
      out.push(c);
    }
    return out.reverse();
  }
  const mid = Math.floor(n / 2);
  const c = keyBetween(a, b);
  return [...keysBetween(a, c, mid), c, ...keysBetween(c, b, n - mid - 1)];
}

export const FIRST_INDEX = ZERO;

/** Ascending comparator on the index field, with id as a stable tiebreak. */
export function compareIndex(
  a: { index: string; id: string },
  b: { index: string; id: string },
): number {
  if (a.index < b.index) return -1;
  if (a.index > b.index) return 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
